import React, { useState, useEffect, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];
const RADIAN = Math.PI / 180;

const AreaDetailPieChart = ({ formatCurrency }) => {
  const [areasData, setAreasData] = useState({});
  const [expandedAreas, setExpandedAreas] = useState({});
  const [chartSize, setChartSize] = useState({ width: 300, height: 300 });
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAreasData = async () => {
      try {
        const budgetsCollection = collection(db, 'budgets');
        const querySnapshot = await getDocs(budgetsCollection);
        const budgets = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const areas = budgets.filter(budget => budget.type === 'area');
        const subAreas = budgets.filter(budget => budget.type === 'subarea');

        const fetchedAreasData = {};
        for (const area of areas) {
          const areaSubAreas = subAreas.filter(subArea => subArea.from === area.id);
          const subAreasData = {};

          for (const subArea of areaSubAreas) {
            const subAreaCollection = collection(db, subArea.id);
            const subAreaSnapshot = await getDocs(subAreaCollection);
            const obras = subAreaSnapshot.docs.map(doc => ({
              name: doc.data().obra,
              value: doc.data().costo || 0
            }));

            const usedBudget = obras.reduce((sum, obra) => sum + obra.value, 0);
            const remainingBudget = Math.max(subArea.amount - usedBudget, 0);

            subAreasData[subArea.id] = {
              name: subArea.id,
              amount: subArea.amount,
              obras,
              usedBudget,
              remainingBudget
            };
          }

          const areaCollection = collection(db, area.id);
          const areaSnapshot = await getDocs(areaCollection);
          const areaProjects = areaSnapshot.docs.map(doc => ({
            name: doc.data().obra,
            value: doc.data().costo || 0
          }));

          fetchedAreasData[area.id] = {
            name: area.id,
            amount: area.amount,
            subAreas: subAreasData,
            used: area.used || 0,
            usedarea: area.usedarea || 0,
            projects: areaProjects
          };
        }

        setAreasData(fetchedAreasData);
      } catch (error) {
        console.error("Error fetching areas data:", error);
      }
    };

    fetchAreasData();
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        const size = Math.min(width, 400);
        setChartSize({ width: size, height: size });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const handleBudgetClick = (area, subArea) => {
    navigate('/admin/control', { state: { selectedArea: area, specificArea: subArea || area } });
  };

  const toggleExpanded = (areaId) => {
    setExpandedAreas(prev => ({
      ...prev,
      [areaId]: !prev[areaId]
    }));
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          stroke: "grey",
          strokeWidth: "1px",
          paintOrder: "stroke"
        }}
      >
        {`${(percent * 100).toFixed(2)}%`}
      </text>
    );
  };

  const mainChartData = Object.entries(areasData).map(([areaId, areaData]) => ({
    name: areaData.name,
    value: areaData.amount
  }));

  const totalBudget = mainChartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="areas-detail-charts" ref={containerRef}>
      <div className='container-invisible'>
        <div className="main-area-chart">
          <h3 className='areaName'>Distribución de Presupuesto por Áreas</h3>
          <div className="container-center">
            <div className="pie-container" style={{ width: '100%', height: chartSize.height }}>
              <ResponsiveContainer className="res-container">
                <p className='totalBudget'>Presupuesto Total: {formatCurrency(totalBudget)}</p>
                <PieChart>
                  <Pie
                    data={mainChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={chartSize.width / 4}
                    innerRadius={chartSize.width / 6}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {mainChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const item = payload[0].payload;
                        const percentage = (item.value / totalBudget) * 100;
                        return (
                          <div className='area-hover'>
                            <p>{item.name}</p>
                            <p>{formatCurrency(item.value)} ({percentage.toFixed(2)}%)</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend 
                    formatter={(value, entry) => {
                      const item = mainChartData.find(d => d.name === value);
                      const percentage = (item.value / totalBudget) * 100;
                      return `${value} ${formatCurrency(item.value)} (${percentage.toFixed(2)}%)`;
                    }}
                    wrapperStyle={{ fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreaDetailPieChart;

